import React, { PropsWithChildren } from "react";

import {
  getIPLookup,
  type IPLookupResponse,
  NON_EU_COUNTRIES_WITH_GDPR,
  type Subdivision,
} from "./IPLookup";

export const EUROZONE_COUNTRIES = [
  // European Union countries
  "AT", // Austria
  "BE", // Belgium
  "CY", // Cyprus
  "EE", // Estonia
  "FI", // Finland
  "FR", // France
  "DE", // Germany
  "GR", // Greece
  "IE", // Ireland
  "IT", // Italy
  "LV", // Latvia
  "LT", // Lithuania
  "LU", // Luxemmbourgh
  "MT", // Malta
  "NL", // Netherlands
  "PT", // Portugal
  "SK", // Slovakia
  "SI", // Slovenia
  "ES", // Spain

  // Non-EU countries
  "AD", // Andorra
  "MC", // Monaco
  "SM", // San Marino
  "VA", // Vatican City
];

export interface IPContext {
  ipInfo: IPLookupResponse | null;
  ipInfoLoaded: boolean;
  isInUSA: boolean;
  isInCA: boolean;
  isInGDPR: boolean;
  isInEurozone: boolean;
  isInWaNv: boolean;
}

export const LookupIPContext = React.createContext<IPContext>({
  ipInfo: null,
  ipInfoLoaded: false,
  isInUSA: false,
  isInCA: false,
  isInGDPR: false,
  isInEurozone: false,
  isInWaNv: false,
});

export default function LookupIPProvider({
  children,
  iplookupApiBaseUrl,
  iplookupApiKey,
  skip = false,
  bakedLocation,
}: PropsWithChildren<{
  iplookupApiBaseUrl: string | null | undefined;
  iplookupApiKey: string | undefined;
  skip?: boolean;
  bakedLocation?: IPLookupResponse;
}>) {
  const [ipInfo, setIpInfo] = React.useState<IPLookupResponse | null>(
    bakedLocation ?? null,
  );
  const bakedLocationGiven = !!bakedLocation;
  const [ipInfoLoaded, setIpInfoLoaded] = React.useState(bakedLocationGiven);

  React.useEffect(() => {
    if (!iplookupApiBaseUrl || !iplookupApiKey || skip || bakedLocationGiven) {
      setIpInfoLoaded(true);
      return;
    }
    getIPLookup({ iplookupApiBaseUrl, iplookupApiKey, fetchFn: fetch })
      .then(resp => {
        setIpInfo(resp);
      })
      .catch(() => {
        setIpInfo(null);
      })
      .finally(() => {
        setIpInfoLoaded(true);
      });
  }, [iplookupApiBaseUrl, iplookupApiKey, skip, bakedLocationGiven]);

  return (
    <LookupIPContext.Provider
      value={{
        ipInfo,
        ipInfoLoaded,
        isInUSA: Boolean((ipInfo?.country?.isoCode || "US") === "US"),
        isInCA: Boolean(
          ipInfo?.country?.isoCode === "US" &&
            !!ipInfo?.subdivisions?.find(
              (s): s is Subdivision => s.isoCode === "CA",
            ),
        ),
        isInGDPR: Boolean(
          ipInfo?.country?.isInEuropeanUnion ||
            NON_EU_COUNTRIES_WITH_GDPR.includes(ipInfo?.country?.isoCode || ""),
        ),
        isInEurozone: Boolean(
          EUROZONE_COUNTRIES.includes(ipInfo?.country?.isoCode || ""),
        ),
        isInWaNv: Boolean(
          ipInfo?.country?.isoCode === "US" &&
            !!ipInfo?.subdivisions?.find(
              (s): s is Subdivision => s.isoCode === "NV" || s.isoCode === "WA",
            ),
        ),
      }}
    >
      {children}
    </LookupIPContext.Provider>
  );
}
